/**
 * Scoring Service
 *
 * ADD this file to: datatool_fe-QA/src/services/scoringService.tsx
 */
import {
  getValidationScoringConfigUrl,
  getValidationScoringConfigDefaultsUrl,
  getPaginatedValidationScoringConfigUrl,
  getValidationScoringConfigBackupUrl,
  getComparisonScoringConfigUrl,
  getComparisonScoringConfigDefaultsUrl,
  getPaginatedComparisonScoringConfigUrl,
  getComparisonScoringConfigBackupUrl,
  getRunScoringConfigUrl,
  getPaginatedRunScoringConfigUrl,
  batchRunScoringConfigUrl,
  getGenericRQScoringConfigUrl,
  getPaginatedGenericRQScoringConfigUrl,
  getGenericRQScoringConfigBackupUrl,
  getValidationScoreUrl,
  getValidationScoreByResourceUrl,
  getComparisonScoreUrl,
  getComparisonScoreByRuleUrl,
  getRunScoreUrl,
  getGenericRQScoreUrl,
  getGenericRQScoreHistoryUrl,
  recalculateValidationScoreUrl,
  recalculateComparisonScoreUrl,
  recalculateRunScoreUrl,
  recalculateGenericRQScoreUrl,
  getValidationRunScoringConfigUrl,
  getPaginatedValidationRunScoringConfigUrl,
  getValidationRunScoringConfigDefaultsUrl,
  getValidationRunScoringConfigBackupUrl,
  getRunScoringPipelineWeightConfigUrl,
  getPaginatedRunScoringPipelineWeightConfigUrl,
  getRunScoringPipelineWeightConfigDefaultsUrl,
  getAggregatedRunScoreUrl,
  generateAggregatedRunScoreUrl,
  getComparisonRunScoringConfigUrl,
  getPaginatedComparisonRunScoringConfigUrl,
  getComparisonRunScoringConfigDefaultsUrl,
  batchComparisonRunScoringConfigUrl,
  getResearchQueryRunScoringConfigUrl,
  getPaginatedResearchQueryRunScoringConfigUrl,
  getResearchQueryRunScoringConfigDefaultsUrl,
  batchResearchQueryRunScoringConfigUrl,
} from "./constants";
import base from "../middlewares/interceptors";

// ═══════════════════════════════════════════════════════════════
// Validation Scoring Config
// ═══════════════════════════════════════════════════════════════

export const getValidationScoringConfigDefaults = async () => {
  try {
    const response = await base.get(getValidationScoringConfigDefaultsUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getValidationScoringConfigByResource = async (
  resourceId: number,
) => {
  try {
    const response = await base.get(
      `${getValidationScoringConfigUrl}/resource/${resourceId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedValidationScoringConfigs = async ({
  page,
  pSize,
  resourceId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (resourceId != null) params.set("resource_id", String(resourceId));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedValidationScoringConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createValidationScoringConfig = async (reqBody: any) => {
  try {
    const response = await base.post(getValidationScoringConfigUrl, reqBody);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateValidationScoringConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getValidationScoringConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteValidationScoringConfig = async (configId: number) => {
  try {
    const response = await base.delete(
      `${getValidationScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reactivateValidationScoringConfig = async (configId: number) => {
  try {
    const response = await base.put(
      `${getValidationScoringConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getValidationScoringConfigById = async (configId: number) => {
  try {
    const response = await base.get(
      `${getValidationScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getValidationScoringConfigBackupByResource = async ({
  resourceId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getValidationScoringConfigBackupUrl}/pages?resource_filter_ids=${resourceId}&page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Comparison Scoring Config
// ═══════════════════════════════════════════════════════════════

export const getComparisonScoringConfigDefaults = async () => {
  try {
    const response = await base.get(getComparisonScoringConfigDefaultsUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getComparisonScoringConfigByRule = async (ruleId: number) => {
  try {
    const response = await base.get(
      `${getComparisonScoringConfigUrl}/rule/${ruleId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedComparisonScoringConfigs = async ({
  page,
  pSize,
  ruleId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (ruleId != null) params.set("rule_id", String(ruleId));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedComparisonScoringConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createComparisonScoringConfig = async (reqBody: any) => {
  try {
    const response = await base.post(getComparisonScoringConfigUrl, reqBody);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateComparisonScoringConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getComparisonScoringConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteComparisonScoringConfig = async (configId: number) => {
  try {
    const response = await base.delete(
      `${getComparisonScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reactivateComparisonScoringConfig = async (configId: number) => {
  try {
    const response = await base.put(
      `${getComparisonScoringConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getComparisonScoringConfigById = async (configId: number) => {
  try {
    const response = await base.get(
      `${getComparisonScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getComparisonScoringConfigBackupByRule = async ({
  ruleId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getComparisonScoringConfigBackupUrl}/pages?rule_filter_ids=${ruleId}&page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedRunScoringConfigs = async ({
  page,
  pSize,
  runInstanceId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (runInstanceId != null)
      params.set("run_instance_id", String(runInstanceId));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedRunScoringConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Run Scoring Config
// ═══════════════════════════════════════════════════════════════

export const getRunScoringConfigs = async (runInstanceId: number) => {
  try {
    const response = await base.get(
      `${getRunScoringConfigUrl}/${runInstanceId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createRunScoringConfig = async (reqBody: any) => {
  try {
    const response = await base.post(getRunScoringConfigUrl, reqBody);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateRunScoringConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getRunScoringConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const deleteRunScoringConfig = async (configId: number) => {
  try {
    const response = await base.delete(`${getRunScoringConfigUrl}/${configId}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const reactivateRunScoringConfig = async (configId: number) => {
  try {
    const response = await base.put(
      `${getRunScoringConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const batchCreateOrUpdateRunScoringConfig = async (
  runInstanceId: number,
  reqBody: any[],
) => {
  try {
    const response = await base.post(
      `${batchRunScoringConfigUrl}/${runInstanceId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Score Results
// ═══════════════════════════════════════════════════════════════

export const getValidationScore = async (executionId: number) => {
  try {
    const response = await base.get(`${getValidationScoreUrl}/${executionId}`);
    return response.data;
  } catch (error) {
    // 404 (score not found) is expected for executions without a calculated score
    return null;
  }
};

export const getLatestValidationScoreByResource = async (
  resourceId: number,
) => {
  try {
    const response = await base.get(
      `${getValidationScoreByResourceUrl}/${resourceId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getComparisonScore = async (executionId: number) => {
  try {
    const response = await base.get(`${getComparisonScoreUrl}/${executionId}`);
    return response.data;
  } catch (error) {
    // 404 (score not found) is expected for executions without a calculated score
    return null;
  }
};

export const getLatestComparisonScoreByRule = async (ruleId: number) => {
  try {
    const response = await base.get(`${getComparisonScoreByRuleUrl}/${ruleId}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getRunScore = async (runId: number) => {
  try {
    const response = await base.get(`${getRunScoreUrl}/${runId}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getRunScoreHistory = async ({ runId, page, pSize }: any) => {
  try {
    const response = await base.get(
      `${getRunScoreUrl}/${runId}/history/pages?page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getValidationScoreHistory = async ({
  executionId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getValidationScoreUrl}/${executionId}/history/pages?page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getValidationExecutionHistory = async ({
  executionId,
  page,
  pSize,
}: any) => {
  try {
    const params = new URLSearchParams();
    if (executionId != null) params.set("execution_id", String(executionId));
    params.set("page", String(page));
    params.set("size", String(pSize));
    const response = await base.get(
      `${getValidationScoreUrl}/history/pages?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getValidationScoreHistoryByResource = async ({
  resourceId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getValidationScoreByResourceUrl}/${resourceId}/history/pages?page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getComparisonScoreHistory = async ({
  executionId,
  page,
  pSize,
}: any) => {
  try {
    const params = new URLSearchParams();
    if (executionId != null) params.set("execution_id", String(executionId));
    params.set("page", String(page));
    params.set("size", String(pSize));
    const response = await base.get(
      `${getComparisonScoreUrl}/history/pages?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getComparisonScoreHistoryByRule = async ({
  ruleId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getComparisonScoreByRuleUrl}/${ruleId}/history/pages?page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getGRQScoreHistory = async ({ executionId, page, pSize }: any) => {
  try {
    const params = new URLSearchParams();
    if (executionId != null) params.set("execution_id", String(executionId));
    params.set("page", String(page));
    params.set("size", String(pSize));
    const response = await base.get(
      `${getGenericRQScoreHistoryUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getGRQScoreHistoryByQuery = async ({
  queryId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getGenericRQScoreUrl}/query/${queryId}/history/pages?page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Recalculation
// ═══════════════════════════════════════════════════════════════

export const recalculateValidationScore = async (
  executionId: number,
  configOverride?: Record<string, any>,
) => {
  try {
    const response = await base.post(
      `${recalculateValidationScoreUrl}/${executionId}`,
      configOverride || {},
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const recalculateComparisonScore = async (
  executionId: number,
  configOverride?: Record<string, any>,
) => {
  try {
    const response = await base.post(
      `${recalculateComparisonScoreUrl}/${executionId}`,
      configOverride || {},
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const recalculateRunScore = async (runId: number) => {
  try {
    const response = await base.post(`${recalculateRunScoreUrl}/${runId}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getGenericRQScore = async (grqResultId: number) => {
  try {
    const response = await base.get(`${getGenericRQScoreUrl}/${grqResultId}`);
    return response.data;
  } catch (error) {
    // 404 (score not found) is expected for GRQ executions without a calculated score
    return null;
  }
};

export const recalculateGenericRQScore = async (
  grqResultId: number,
  configOverride?: Record<string, any>,
) => {
  try {
    const response = await base.post(
      `${recalculateGenericRQScoreUrl}/${grqResultId}`,
      configOverride || {},
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Generic RQ Scoring Config
// ═══════════════════════════════════════════════════════════════

export const getPaginatedGenericRQScoringConfigs = async ({
  page,
  pSize,
  genericResearchQueryId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (genericResearchQueryId != null) {
      params.set("generic_research_query_id", String(genericResearchQueryId));
    }
    if (isActive != null) {
      params.set("is_active", String(isActive));
    }
    const response = await base.get(
      `${getPaginatedGenericRQScoringConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getGenericRQScoringConfigById = async (configId: number) => {
  try {
    const response = await base.get(
      `${getGenericRQScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getGenericRQScoringConfigByQuery = async (queryId: number) => {
  try {
    const response = await base.get(
      `${getGenericRQScoringConfigUrl}/research-query/${queryId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createGenericRQScoringConfig = async (reqBody: any) => {
  try {
    const response = await base.post(getGenericRQScoringConfigUrl, reqBody);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateGenericRQScoringConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getGenericRQScoringConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteGenericRQScoringConfig = async (configId: number) => {
  try {
    const response = await base.delete(
      `${getGenericRQScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reactivateGenericRQScoringConfig = async (configId: number) => {
  try {
    const response = await base.put(
      `${getGenericRQScoringConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getGenericRQScoringConfigBackupByQuery = async ({
  queryId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getGenericRQScoringConfigBackupUrl}/pages?grq_filter_ids=${queryId}&page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Validation Run Scoring Config
// ═══════════════════════════════════════════════════════════════

export const getValidationRunScoringConfigDefaults = async () => {
  try {
    const response = await base.get(getValidationRunScoringConfigDefaultsUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedValidationRunScoringConfigs = async ({
  page,
  pSize,
  runInstanceId,
  resourceId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (runInstanceId != null)
      params.set("run_instance_id", String(runInstanceId));
    if (resourceId != null) params.set("resource_id", String(resourceId));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedValidationRunScoringConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createValidationRunScoringConfig = async (reqBody: any) => {
  try {
    const response = await base.post(getValidationRunScoringConfigUrl, reqBody);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateValidationRunScoringConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getValidationRunScoringConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getValidationRunScoringConfigById = async (configId: number) => {
  try {
    const response = await base.get(
      `${getValidationRunScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const deleteValidationRunScoringConfig = async (configId: number) => {
  try {
    const response = await base.delete(
      `${getValidationRunScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reactivateValidationRunScoringConfig = async (
  configId: number,
) => {
  try {
    const response = await base.put(
      `${getValidationRunScoringConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getValidationRunScoringConfigBackup = async ({
  runId,
  resourceId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getValidationRunScoringConfigBackupUrl}/run/${runId}/resource/${resourceId}?page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Comparison Run Scoring Config
// ═══════════════════════════════════════════════════════════════

export const getComparisonRunScoringConfigDefaults = async () => {
  try {
    const response = await base.get(getComparisonRunScoringConfigDefaultsUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedComparisonRunScoringConfigs = async ({
  page,
  pSize,
  runInstanceId,
  ruleId,
  severity,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (runInstanceId != null)
      params.set("run_instance_id", String(runInstanceId));
    if (ruleId != null) params.set("rule_id", String(ruleId));
    if (severity) params.set("severity", String(severity));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedComparisonRunScoringConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createComparisonRunScoringConfig = async (reqBody: any) => {
  try {
    const response = await base.post(getComparisonRunScoringConfigUrl, reqBody);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateComparisonRunScoringConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getComparisonRunScoringConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteComparisonRunScoringConfig = async (configId: number) => {
  try {
    const response = await base.delete(
      `${getComparisonRunScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reactivateComparisonRunScoringConfig = async (
  configId: number,
) => {
  try {
    const response = await base.put(
      `${getComparisonRunScoringConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getComparisonRunScoringConfigById = async (configId: number) => {
  try {
    const response = await base.get(
      `${getComparisonRunScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getComparisonRunScoringConfigsForRun = async (
  runInstanceId: number,
) => {
  try {
    const response = await base.get(
      `${getComparisonRunScoringConfigUrl}/${runInstanceId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const batchCreateOrUpdateComparisonRunScoringConfig = async (
  runInstanceId: number,
  reqBody: any[],
) => {
  try {
    const response = await base.post(
      `${batchComparisonRunScoringConfigUrl}/${runInstanceId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════
// Run Scoring Pipeline Weight Config
// ═══════════════════════════════════════════════════════════════

export const getRunScoringPipelineWeightConfigDefaults = async () => {
  try {
    const response = await base.get(
      getRunScoringPipelineWeightConfigDefaultsUrl,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedRunScoringPipelineWeightConfigs = async ({
  page,
  pSize,
  runInstanceId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (runInstanceId != null)
      params.set("run_instance_id", String(runInstanceId));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedRunScoringPipelineWeightConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createRunScoringPipelineWeightConfig = async (reqBody: any) => {
  try {
    const response = await base.post(
      getRunScoringPipelineWeightConfigUrl,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateRunScoringPipelineWeightConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getRunScoringPipelineWeightConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getRunScoringPipelineWeightConfigById = async (
  configId: number,
) => {
  try {
    const response = await base.get(
      `${getRunScoringPipelineWeightConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getRunScoringPipelineWeightConfigsForRun = async (
  runId: number,
) => {
  try {
    const response = await base.get(
      `${getRunScoringPipelineWeightConfigUrl}/run/${runId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ─── Clean-named aliases + missing CRUD for Pipeline Weight Config ───

export const getPipelineWeightConfigByRunId = async (runId: number) => {
  try {
    const response = await base.get(
      `${getRunScoringPipelineWeightConfigUrl}/${runId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedPipelineWeightConfigs = async ({
  page,
  pSize,
  runInstanceId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (runInstanceId != null)
      params.set("run_instance_id", String(runInstanceId));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedRunScoringPipelineWeightConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createPipelineWeightConfig = async (reqBody: any) => {
  try {
    const response = await base.post(
      getRunScoringPipelineWeightConfigUrl,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updatePipelineWeightConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getRunScoringPipelineWeightConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deletePipelineWeightConfig = async (configId: number) => {
  try {
    const response = await base.delete(
      `${getRunScoringPipelineWeightConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reactivatePipelineWeightConfig = async (configId: number) => {
  try {
    const response = await base.put(
      `${getRunScoringPipelineWeightConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════
// Aggregated Run Score (datatool_ee)
// ═══════════════════════════════════════════════════════════════

export const getAggregatedRunScore = async (runId: number, date?: string) => {
  try {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const response = await base.get(
      `${getAggregatedRunScoreUrl}/${runId}${qs}`,
    );
    return response.data;
  } catch (error) {
    // 404 is expected for runs without an aggregated score
    return null;
  }
};

export const generateAggregatedRunScore = async (
  runId: number,
  date?: string,
) => {
  try {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const response = await base.post(
      `${generateAggregatedRunScoreUrl}/${runId}${qs}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getAggregatedRunScoreHistory = async ({
  runId,
  page,
  pSize,
}: any) => {
  try {
    const response = await base.get(
      `${getAggregatedRunScoreUrl}/${runId}/history?page=${page}&size=${pSize}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// ═══════════════════════════════════════════════════════════════
// Research Query Run Scoring Config
// ═══════════════════════════════════════════════════════════════

export const getResearchQueryRunScoringConfigDefaults = async () => {
  try {
    const response = await base.get(
      getResearchQueryRunScoringConfigDefaultsUrl,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getPaginatedResearchQueryRunScoringConfigs = async ({
  page,
  pSize,
  runInstanceId,
  genericResearchQueryId,
  isActive,
}: any) => {
  try {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pSize));
    if (runInstanceId != null)
      params.set("run_instance_id", String(runInstanceId));
    if (genericResearchQueryId != null)
      params.set("generic_research_query_id", String(genericResearchQueryId));
    if (isActive != null) params.set("is_active", String(isActive));
    const response = await base.get(
      `${getPaginatedResearchQueryRunScoringConfigUrl}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createResearchQueryRunScoringConfig = async (reqBody: any) => {
  try {
    const response = await base.post(
      getResearchQueryRunScoringConfigUrl,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateResearchQueryRunScoringConfig = async (
  configId: number,
  reqBody: any,
) => {
  try {
    const response = await base.put(
      `${getResearchQueryRunScoringConfigUrl}/${configId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteResearchQueryRunScoringConfig = async (configId: number) => {
  try {
    const response = await base.delete(
      `${getResearchQueryRunScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reactivateResearchQueryRunScoringConfig = async (
  configId: number,
) => {
  try {
    const response = await base.put(
      `${getResearchQueryRunScoringConfigUrl}/${configId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getResearchQueryRunScoringConfigById = async (
  configId: number,
) => {
  try {
    const response = await base.get(
      `${getResearchQueryRunScoringConfigUrl}/${configId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getResearchQueryRunScoringConfigsForRun = async (
  runInstanceId: number,
) => {
  try {
    const response = await base.get(
      `${getResearchQueryRunScoringConfigUrl}/${runInstanceId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const batchCreateOrUpdateResearchQueryRunScoringConfig = async (
  runInstanceId: number,
  reqBody: any[],
) => {
  try {
    const response = await base.post(
      `${batchResearchQueryRunScoringConfigUrl}/${runInstanceId}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
